import { pageViewTypes } from "@constants/tracking";
import {
	defaultFeedbackSubtitle,
	dialogTexts,
} from "@features/feedback/constants";
import { useFeedbackParams } from "@features/feedback/hooks/useFeedbackParams";
import useAnalytics from "@hooks/useAnalytics";
import { useSendFeedback } from "@memorang/features/src/feedback/hooks/useSendFeedback";
import { getFeedbackReasonsByType } from "@memorang/helpers/feedback";
import Box from "@memorang/ui/components/Box";
import Button from "@memorang/ui/components/Button";
import CustomAppBar from "@memorang/ui/components/CustomAppbar";
import { FeedbackContent } from "@memorang/ui/components/FeedbackDialog/FeedbackContent";
import ScrollContainer from "@memorang/ui/components/ScrollContainer";
import { useToast } from "@memorang/ui/components/Toast";
import type { IconType } from "@memorang/ui/icon";
/* -----------------Globals--------------- */
import { Stack, useLocalSearchParams, useRouter } from "expo-router";
import { useMemo, useRef, useState } from "react";
import { useTranslation } from "react-i18next";

const FeedbackRoute = () => {
	useAnalytics({
		type: pageViewTypes.feedback,
	});

	const {
		view,
		itemId,
		sessionId,
		threadId,
		runId,
		type,
		featureName,
		feedbackCta,
		feedbackTitle,
		feedbackSubtitle: feedbackSubtitleParam,
		itemFeedbackRating,
		placeholder,
		feedbackReasonType,
	} = useLocalSearchParams<{
		view?: string;
		itemId?: string;
		sessionId?: string;
		threadId?: string;
		runId?: string;
		type?: string;
		featureName?: string;
		feedbackCta?: string;
		feedbackTitle?: string;
		feedbackSubtitle?: string;
		feedbackIcon?: string;
		itemFeedbackRating?: string;
		placeholder?: string;
		feedbackReasonType?: string;
	}>();
	const router = useRouter();
	const { showToast } = useToast();
	const feedbackContentRef = useRef<{ submit: () => void }>(null);
	const [sendingFeedback, setSendingFeedback] = useState(false);
	const [isValid, setIsValid] = useState(false);

	const config = dialogTexts.feedback;
	const { t } = useTranslation();

	const feedbackDialogParams = useFeedbackParams({
		view: view || "profile",
		type: (type as "Review" | "Content Flagged") || undefined,
		itemId,
		sessionId,
		threadId,
		runId,
		rating: itemFeedbackRating,
		featureName,
	});

	const handleDismissFeedback = () => {
		router.back();
	};

	const sendFeedback = useSendFeedback();

	const finalTitle = feedbackTitle || t(config.title);
	const finalSubtitle = feedbackSubtitleParam || t(defaultFeedbackSubtitle);
	const finalCta = feedbackCta || t("dialog.feedback.cta");
	const finalPlaceholder = placeholder || t("dialog.feedback.placeholder");
	const finalSuccessMessage = t("feedback.successMessage");
	const finalLimitError = t(
		"report.sessionAnalysis.feedbackDialog.feedbackLimitError",
	);

	const feedbackReasonList = useMemo(
		() =>
			getFeedbackReasonsByType<IconType>(
				feedbackReasonType as "negative" | "report" | undefined,
				t,
			),
		[feedbackReasonType, t],
	);

	return (
		<>
			<Stack.Screen
				options={{
					presentation: "modal",
					headerShown: false,
				}}
			/>
			<CustomAppBar
				title={finalTitle}
				leftIconType="back"
				onPressBack={handleDismissFeedback}
			/>
			<Box flex={1}>
				<ScrollContainer keyboardShouldPersistTaps="handled">
					<Box paddingTop={16}>
						<FeedbackContent
							ref={feedbackContentRef}
							sendFeedback={sendFeedback}
							feedbackDialogParams={feedbackDialogParams}
							onDismiss={handleDismissFeedback}
							subtitle={finalSubtitle}
							feedbackPlaceholder={finalPlaceholder}
							feedbackLimitError={finalLimitError}
							feedbackReasonList={feedbackReasonList}
							showFeedbackConfirmationToast={() =>
								showToast({
									message: finalSuccessMessage,
									duration: 3_000,
								})
							}
							onStateChange={(sending, valid) => {
								setSendingFeedback(sending);
								setIsValid(valid);
							}}
						/>
					</Box>
					<Box padding={16}>
						<Button
							onPress={() => feedbackContentRef.current?.submit()}
							loading={sendingFeedback}
							disabled={!isValid}
							mode="contained"
						>
							{finalCta}
						</Button>
					</Box>
				</ScrollContainer>
			</Box>
		</>
	);
};

export default FeedbackRoute;
